import React, { useState, useEffect } from 'react';
import { Users, FileText, Image, Brain, AlertTriangle, Activity, TrendingUp, Clock } from 'lucide-react';

const BetaDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBetaStats();
    // Refresh every 30 seconds
    const interval = setInterval(fetchBetaStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchBetaStats = async () => {
    try {
      const response = await fetch('/api/admin/beta/stats', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch beta stats');
      }
      
      const data = await response.json();
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-32 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error}</p>
          <button 
            onClick={fetchBetaStats}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">
          🚀 Closed Beta Dashboard
        </h1>
        <p className="text-slate-600">
          Real-time monitoring • Last updated: {new Date(stats?.timestamp).toLocaleTimeString()}
        </p>
      </div>

      {/* User Metrics */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-700 mb-4">👥 User Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatsCard
            title="Beta Testers"
            value={stats?.users?.total_beta_testers || 0}
            icon={<Users className="text-blue-600" size={24} />}
            bgColor="bg-blue-50"
            borderColor="border-blue-200"
          />
          <StatsCard
            title="New Accounts (7d)"
            value={stats?.users?.new_accounts_7d || 0}
            icon={<TrendingUp className="text-green-600" size={24} />}
            bgColor="bg-green-50"
            borderColor="border-green-200"
            subtitle={`${stats?.users?.engagement_rate_7d || 0}% engagement`}
          />
          <StatsCard
            title="Active Today"
            value={stats?.metrics?.dau || 0}
            icon={<Activity className="text-purple-600" size={24} />}
            bgColor="bg-purple-50"
            borderColor="border-purple-200"
          />
          <StatsCard
            title="Active This Week"
            value={stats?.metrics?.wau || 0}
            icon={<Clock className="text-orange-600" size={24} />}
            bgColor="bg-orange-50"
            borderColor="border-orange-200"
          />
        </div>
      </div>

      {/* Content Metrics */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-700 mb-4">📝 Content Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatsCard
            title="Products Created"
            value={stats?.content?.total_products || 0}
            icon={<FileText className="text-indigo-600" size={24} />}
            bgColor="bg-indigo-50"
            borderColor="border-indigo-200"
            subtitle={`+${stats?.content?.products_created_7d || 0} this week`}
          />
          <StatsCard
            title="Images Uploaded"
            value={stats?.content?.total_images_estimate || 0}
            icon={<Image className="text-pink-600" size={24} />}
            bgColor="bg-pink-50"
            borderColor="border-pink-200"
            subtitle={`${stats?.content?.products_with_images || 0} products with images`}
          />
          <StatsCard
            title="Publications"
            value={stats?.publications?.total || 0}
            icon={<FileText className="text-teal-600" size={24} />}
            bgColor="bg-teal-50"
            borderColor="border-teal-200"
            subtitle={`+${stats?.publications?.created_7d || 0} this week`}
          />
          <StatsCard
            title="Content Rate"
            value={stats?.metrics?.content_creation_rate_7d?.toFixed(1) || '0.0'}
            icon={<TrendingUp className="text-emerald-600" size={24} />}
            bgColor="bg-emerald-50"
            borderColor="border-emerald-200"
            subtitle="products/user/week"
          />
        </div>
      </div>

      {/* AI Metrics */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-700 mb-4">🤖 AI Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatsCard
            title="AI Executions"
            value={stats?.ai?.total_executions || 0}
            icon={<Brain className="text-violet-600" size={24} />}
            bgColor="bg-violet-50"
            borderColor="border-violet-200"
            subtitle={`+${stats?.ai?.executions_7d || 0} this week`}
          />
          <StatsCard
            title="AI Usage Rate"
            value={stats?.ai?.ai_usage_rate_7d?.toFixed(1) || '0.0'}
            icon={<Activity className="text-fuchsia-600" size={24} />}
            bgColor="bg-fuchsia-50"
            borderColor="border-fuchsia-200"
            subtitle="executions/user/week"
          />
          <StatsCard
            title="Generation History"
            value={stats?.ai?.total_executions || 0}
            icon={<Clock className="text-amber-600" size={24} />}
            bgColor="bg-amber-50"
            borderColor="border-amber-200"
            subtitle="total generations"
          />
        </div>
      </div>

      {/* System Health */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-700 mb-4">⚙️ System Health</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatsCard
            title="Database Status"
            value={stats?.system?.database_status === 'healthy' ? 'Healthy' : 'Unhealthy'}
            icon={<Activity className={stats?.system?.database_status === 'healthy' ? 'text-green-600' : 'text-red-600'} size={24} />}
            bgColor={stats?.system?.database_status === 'healthy' ? 'bg-green-50' : 'bg-red-50'}
            borderColor={stats?.system?.database_status === 'healthy' ? 'border-green-200' : 'border-red-200'}
          />
          <StatsCard
            title="Backend Errors (7d)"
            value={stats?.system?.backend_errors_7d || 0}
            icon={<AlertTriangle className="text-red-600" size={24} />}
            bgColor="bg-red-50"
            borderColor="border-red-200"
            subtitle={stats?.system?.note || ''}
            subtitleClass="text-xs text-slate-500 italic"
          />
          <StatsCard
            title="Frontend Errors (7d)"
            value={stats?.system?.frontend_errors_7d || 0}
            icon={<AlertTriangle className="text-orange-600" size={24} />}
            bgColor="bg-orange-50"
            borderColor="border-orange-200"
            subtitle="Requires Sentry DSN"
            subtitleClass="text-xs text-slate-500 italic"
          />
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>📊 Closed Beta Status:</strong> Active • Monitoring enabled • Code Freeze active
        </p>
        <p className="text-xs text-blue-600 mt-1">
          Metrics refresh every 30 seconds. Error tracking requires Sentry DSN configuration.
        </p>
      </div>
    </div>
  );
};

// Stats Card Component
const StatsCard = ({ 
  title, 
  value, 
  icon, 
  bgColor = 'bg-white', 
  borderColor = 'border-slate-200',
  subtitle,
  subtitleClass = 'text-sm text-slate-600'
}) => {
  return (
    <div className={`${bgColor} border ${borderColor} rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-medium text-slate-600">{title}</h3>
        {icon}
      </div>
      <p className="text-3xl font-bold text-slate-900 mb-1">{value}</p>
      {subtitle && (
        <p className={subtitleClass}>{subtitle}</p>
      )}
    </div>
  );
};

export default BetaDashboard;
